import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Edit as EditIcon,
  KeyboardArrowDown,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { SupplierSelect } from "@/features/suppliers";
import {
  PurchaseOrderStatusAutocomplete,
  statusColors,
  StatusEnum,
  statusLabels,
} from "@/features/purchase-orders";
import { apiOutletClient, formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const PurchaseOrders = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const statusValue = useMemo(() => searchParams.get("status"), [searchParams]);
  const prevOutletRoleRef = useRef();

  // Parse seller from URL params
  const seller = useMemo(() => {
    const sellerParam = searchParams.get("seller");
    if (!sellerParam) return null;
    try {
      return JSON.parse(sellerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadPurchaseOrders = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("purchase-orders/standard", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          status: statusValue || null,
          sellerableId: seller?.id || null,
          withSeller: true,
          withCreator: true,
        },
      });
      setPurchaseOrders(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pesanan pembelian"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    statusValue,
    keyword,
    seller,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  // Reset seller when outlet role changes
  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("seller");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleSellerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "seller",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("seller");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleClose();
    navigate(path);
  };

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_PURCHASE_ORDERS_STANDARD
  ) && (
    <>
      <Button
        fullWidth
        variant="contained"
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
      >
        Tambah
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handleMenuItemClick("create")}>
          Normal
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("create-inverted")}>
          Inverted
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <PageContainer
      title="Pesanan Pembelian Bulk"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <PurchaseOrderStatusAutocomplete
              value={
                statusValue
                  ? {
                      value: statusValue,
                      label: statusLabels[statusValue],
                    }
                  : null
              }
              onChange={(_, selectedOption) => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  if (selectedOption?.value) {
                    newParams.set("status", selectedOption.value);
                  } else {
                    newParams.delete("status");
                  }
                  resetParams.forEach((param) => newParams.delete(param));
                  return newParams;
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <SupplierSelect
                value={seller}
                onChange={handleSellerChange}
                TextFieldProps={{
                  label: "Pemasok (Supplier)",
                }}
              />
            ) : (
              <AllOutletsSelect
                value={seller}
                onChange={handleSellerChange}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok (Hub)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Pembeli",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={purchaseOrders}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (transactionDate) => formatDate(transactionDate),
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (creator) => creator?.name ?? "-",
            },
            {
              field: "seller",
              headerName: "Pemasok",
              flex: 1.5,
              sortable: false,
              valueGetter: (seller) => seller?.name ?? "-",
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1,
              sortable: false,
              valueGetter: (note) => note ?? "-",
            },
            {
              field: "grandTotal",
              headerName: "Total",
              flex: 1,
              type: "number",
              sortable: false,
              valueGetter: (grandTotal) => formatNumber(grandTotal),
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row: { referenceNumber, status } }) => {
                const actions = [];
                if (
                  hasPermission(PermissionEnum.READ_PURCHASE_ORDERS_STANDARD)
                ) {
                  actions.push(
                    <IconButton
                      key="view"
                      onClick={() => navigate(referenceNumber)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  );
                }
                if (
                  hasPermission(
                    PermissionEnum.UPDATE_PURCHASE_ORDERS_STANDARD
                  ) &&
                  (status === StatusEnum.PENDING ||
                    status === StatusEnum.PARTIALLY_PROCESSED)
                ) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => navigate(`${referenceNumber}/edit`)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
